// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableRow,
  TableCell,
  Divider,
  DialogTitle,
  Chip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
// import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import AppConfig from "appConfig";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";

function BusinessLineCallsPage() {
  const pageTitle = "Call Logs";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const statusDialog = useAwaitDialog();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [calls, setCalls] = useState([]);
  const [call, setCall] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [transcript, setTranscript] = useState(null);

  const tableColumns = [
    { Header: "#", accessor: "direction_icon", align: "left" },
    { Header: "Direction", accessor: "direction", align: "left" },
    { Header: "Status", accessor: "status", align: "left" },
    { Header: "Contact", accessor: "caller", align: "left" },
    { Header: "Duration", accessor: "duration", align: "left" },
    { Header: "Cost", accessor: "charge", align: "left" },
    { Header: "GPS", accessor: "location", align: "center" },
    { Header: "Date", accessor: "date", align: "right" },
  ];

  const buildRateRows = (data, crcy) => {
    function getCallIcon(row) {
      if (row.direction === "inbound") {
        if (row.status === "completed") {
          return "phone_callback";
        } else {
          return "phone_missed";
        }
      } else {
        if (row.status === "completed") {
          return "phone_forwarded";
        } else {
          return "call_end";
        }
      }
    }

    function getCallColor(row) {
      if (row.direction === "inbound") {
        if (row.status === "completed") {
          return "warning";
        } else {
          return "error";
        }
      } else {
        if (row.status === "completed") {
          return "success";
        } else {
          return "dark";
        }
      }
    }
    setCurrency(crcy);
    const rows = [];
    data.map((row) => {
      rows.push({
        direction_icon: <Icon>{getCallIcon(row)}</Icon>,
        direction: row.direction.toUpperCase(),
        status: row.status.toUpperCase(),
        caller: row.caller ?? "---",
        duration: `${row.duration}s`,
        charge: `${crcy}${(row.charge / 100).toLocaleString("en", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        location: row.latitude ? (
          <a
            rel="noreferrer"
            target="_blank"
            href={`https://maps.google.com?q=${row.latitude},${row.longitude}`}
          >
            <Icon>location_on</Icon>
          </a>
        ) : (
          "---"
        ),
        date: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/voice/business_lines/${ref}/calls.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRateRows(result.data.data.calls, result.data.currency);
            setCalls(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={1} mb={5}>
        <MDBox mb={1}>
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/voice/business-lines/${ref}`);
            }}
          >
            Back
          </Button>
        </MDBox>
        {pageLoaded && (
          <MDBox>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={0}>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6">Call Logs</MDTypography>
                  </MDBox>
                  <MDBox>
                    <Tooltip title="Refresh">
                      <MDButton
                        variant="outlined"
                        color="info"
                        iconOnly
                        onClick={() => {
                          setRefreshPage(!refreshPage);
                        }}
                      >
                        <Icon>replay</Icon>
                      </MDButton>
                    </Tooltip>
                  </MDBox>
                </MDBox>
                <MDBox mt={0}>
                  <DataTable
                    table={{ columns: tableColumns, rows: calls }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    buttons
                    noEndBorder
                  />
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default BusinessLineCallsPage;
